import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { StringParam, useQueryParam } from "use-query-params";

import { cn } from "@src/lib/utils/cn";
import {
  TabStripContext,
  TabStripCount,
  TabStripIndicators,
  TabStripScrollFrame,
  useTabStripIndicators,
  useTabStripRegistration,
  useTabStripScroll,
} from "@src/lib/ui/tabStrip";

/**
 * Tabs answer "which view am I looking at?": each tab swaps the region below
 * for a different view. One look: a quiet pill that slides to the hovered or
 * selected tab, plus a 1px underline on the row divider under the selected
 * tab. Built on Radix Tabs (role=tablist / tab / tabpanel; ←/→, Home/End and
 * a roving tabindex). See `## Tabs` in design.md for the rule against
 * SegmentedControl and NavList.
 */

export type TabsSize = "md" | "sm";

type SizeSpec = {
  height: string;
  radius: string;
  weight: string;
  padding: string;
  /** The row height the underline sits at the bottom of. */
  row: string;
};

const SIZES: Record<TabsSize, SizeSpec> = {
  // 32px tab in a 48px row (the shell subheader); 14px / 500.
  md: {
    height: "h-8",
    radius: "rounded-[10px]",
    weight: "font-medium-v4",
    padding: "px-2.5",
    row: "min-h-12",
  },
  // 28px tab in a 40px row (card headers, sidebars, dialogs); 14px / 450.
  sm: {
    height: "h-7",
    radius: "rounded-[9px]",
    weight: "font-normal-v4",
    padding: "px-2",
    row: "min-h-10",
  },
};

type TabsRootContextValue = {
  size: TabsSize;
  value: string | undefined;
  /** Panels by value; true when the panel is force-mounted. */
  panels: Readonly<Record<string, boolean>>;
  registerPanel: (value: string, forceMount: boolean) => () => void;
  registerTrigger?: (value: string) => () => void;
};

const TabsRootContext = React.createContext<TabsRootContextValue | null>(null);

type TabsListContextValue = { fill: boolean };

const TabsListContext = React.createContext<TabsListContextValue>({
  fill: false,
});

type RadixRootProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>;

export type TabsProps = RadixRootProps & {
  size?: TabsSize;
  /**
   * Keep the selected tab in this URL query param (use-query-params,
   * `replaceIn`). `defaultValue` (or `value`) is the tab when the param is
   * missing or names no tab in the list.
   */
  queryParam?: string;
};

type TabsRootProps = Omit<TabsProps, "queryParam"> & {
  registerTrigger?: (value: string) => () => void;
};

const TabsRoot = React.forwardRef<HTMLDivElement, TabsRootProps>(
  function TabsRoot(
    {
      size = "md",
      value: valueProp,
      defaultValue,
      onValueChange,
      registerTrigger,
      ...props
    },
    ref,
  ) {
    // Controlled internally so triggers know which tab is selected (the
    // indicator and aria-controls depend on it), with Radix's semantics: an
    // undefined `value` means uncontrolled.
    const [innerValue, setInnerValue] = React.useState(defaultValue);
    const isControlled = valueProp !== undefined;
    const value = isControlled ? valueProp : innerValue;

    const handleValueChange = React.useCallback(
      (next: string) => {
        if (!isControlled) setInnerValue(next);
        onValueChange?.(next);
      },
      [isControlled, onValueChange],
    );

    const [panels, setPanels] = React.useState<Record<string, boolean>>({});
    const registerPanel = React.useCallback(
      (panelValue: string, forceMount: boolean) => {
        setPanels((prev) =>
          prev[panelValue] === forceMount
            ? prev
            : { ...prev, [panelValue]: forceMount },
        );
        return () =>
          setPanels((prev) => {
            if (!(panelValue in prev)) return prev;
            const next = { ...prev };
            delete next[panelValue];
            return next;
          });
      },
      [],
    );

    const contextValue = React.useMemo(
      () => ({ size, value, panels, registerPanel, registerTrigger }),
      [size, value, panels, registerPanel, registerTrigger],
    );

    return (
      <TabsRootContext.Provider value={contextValue}>
        <TabsPrimitive.Root
          ref={ref}
          value={value}
          onValueChange={handleValueChange}
          {...props}
        />
      </TabsRootContext.Provider>
    );
  },
);

const QueryParamTabs = React.forwardRef<
  HTMLDivElement,
  TabsProps & { queryParam: string }
>(function QueryParamTabs(
  { queryParam, value: fallbackValue, defaultValue, onValueChange, ...props },
  ref,
) {
  const [param, setParam] = useQueryParam(queryParam, StringParam);
  const [triggerValues, setTriggerValues] = React.useState<string[]>([]);

  const registerTrigger = React.useCallback((triggerValue: string) => {
    setTriggerValues((prev) =>
      prev.includes(triggerValue) ? prev : [...prev, triggerValue],
    );
    return () =>
      setTriggerValues((prev) => prev.filter((v) => v !== triggerValue));
  }, []);

  // Like QueryTabs' enum param: a value that names no (enabled) tab shows the
  // default, without touching the URL, so a deep link to a tab that renders
  // or enables once data loads still lands on it. Before the triggers
  // register, trust the URL.
  const fallback = fallbackValue ?? defaultValue ?? triggerValues[0];
  const value =
    param != null &&
    (triggerValues.length === 0 || triggerValues.includes(param))
      ? param
      : fallback;

  const handleValueChange = React.useCallback(
    (next: string) => {
      setParam(next, "replaceIn");
      onValueChange?.(next);
    },
    [onValueChange, setParam],
  );

  return (
    <TabsRoot
      ref={ref}
      // Fully controlled by the URL; "" keeps Radix controlled before a
      // fallback exists.
      value={value ?? ""}
      onValueChange={handleValueChange}
      registerTrigger={registerTrigger}
      {...props}
    />
  );
});

const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(function Tabs(
  { queryParam, ...props },
  ref,
) {
  return queryParam ? (
    <QueryParamTabs ref={ref} queryParam={queryParam} {...props} />
  ) : (
    <TabsRoot ref={ref} {...props} />
  );
});

function useMergedRef<T>(
  ...refs: (React.Ref<T> | undefined)[]
): React.RefCallback<T> {
  const refsRef = React.useRef(refs);
  refsRef.current = refs;
  return React.useCallback((node: T | null) => {
    refsRef.current.forEach((ref) => {
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<T | null>).current = node;
    });
  }, []);
}

export type TabsListProps = React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.List
> & {
  /** Scroll sideways when the tabs overflow, with edge fades and page
      buttons. */
  scroll?: boolean;
  /** Stretch the list and share its width evenly between the tabs. */
  fill?: boolean;
  /** The 1px underline under the selected tab, on the row's bottom edge.
      Turn it off where there is no divider under the row (the title row). */
  underline?: boolean;
};

const TabsList = React.forwardRef<HTMLDivElement, TabsListProps>(
  function TabsList(
    {
      className,
      children,
      scroll = false,
      fill = false,
      underline = true,
      onMouseLeave,
      ...props
    },
    ref,
  ) {
    const root = React.useContext(TabsRootContext);
    const size = SIZES[root?.size ?? "md"];
    const scroller = useTabStripScroll<HTMLDivElement>(
      scroll ? children : null,
    );
    const listRef = useMergedRef<HTMLDivElement>(ref, scroller.scrollRef);
    const {
      setListRef,
      registerTab,
      setHoveredTabId,
      pill,
      underline: line,
    } = useTabStripIndicators<HTMLDivElement>(listRef);

    const stripContext = React.useMemo(
      () => ({ registerTab, setHoveredTabId }),
      [registerTab, setHoveredTabId],
    );
    const listContext = React.useMemo(() => ({ fill }), [fill]);

    const list = (
      <TabsPrimitive.List
        ref={setListRef}
        className={cn(
          "relative flex flex-nowrap items-center gap-1 self-stretch",
          underline && size.row,
          fill && "w-full",
          scroll &&
            "hide-scrollbar w-full min-w-0 overflow-x-auto overflow-y-hidden",
          className,
        )}
        onMouseLeave={(event) => {
          setHoveredTabId(null);
          onMouseLeave?.(event);
        }}
        {...props}
      >
        <TabStripIndicators
          pill={pill}
          underline={line}
          showUnderline={underline}
          heightClass={size.height}
          radiusClass={size.radius}
        />
        {children}
      </TabsPrimitive.List>
    );

    return (
      <TabStripContext.Provider value={stripContext}>
        <TabsListContext.Provider value={listContext}>
          {scroll ? (
            <TabStripScrollFrame
              className="relative flex min-w-0 items-center self-stretch overflow-hidden"
              canScrollLeft={scroller.canScrollLeft}
              canScrollRight={scroller.canScrollRight}
              scrollByPage={scroller.scrollByPage}
            >
              {list}
            </TabStripScrollFrame>
          ) : (
            list
          )}
        </TabsListContext.Provider>
      </TabStripContext.Provider>
    );
  },
);

export type TabsTriggerProps = React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.Trigger
> & {
  /** A count chip after the label. */
  count?: number;
  /** A leading 18px icon, rendered at size-3.5 unless it has a size-*. */
  icon?: React.ReactNode;
};

const TabsTrigger = React.forwardRef<HTMLButtonElement, TabsTriggerProps>(
  function TabsTrigger(
    {
      className,
      value,
      count,
      icon,
      children,
      disabled,
      onMouseEnter,
      ...props
    },
    ref,
  ) {
    const root = React.useContext(TabsRootContext);
    const { fill } = React.useContext(TabsListContext);
    const size = SIZES[root?.size ?? "md"];
    const active = root?.value === value;

    const {
      setRef,
      onMouseEnter: onTabMouseEnter,
      clearHover,
    } = useTabStripRegistration(active);
    const mergedRef = useMergedRef<HTMLButtonElement>(ref, setRef);

    const registerTrigger = root?.registerTrigger;
    // A disabled tab is not a valid URL value.
    React.useLayoutEffect(
      () => (disabled ? undefined : registerTrigger?.(value)),
      [registerTrigger, value, disabled],
    );

    // Radix always points aria-controls at the tab's panel id, even when no
    // panel exists (tabs used as a switcher) or the panel isn't mounted
    // (inactive, not force-mounted). Only keep it when the panel is there.
    const panelForceMounted = root?.panels[value];
    const hasPanel =
      panelForceMounted !== undefined && (panelForceMounted || active);

    return (
      <TabsPrimitive.Trigger
        ref={mergedRef}
        value={value}
        disabled={disabled}
        {...(hasPanel ? {} : { "aria-controls": undefined })}
        className={cn(
          "relative z-[1] inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap",
          size.height,
          size.radius,
          size.weight,
          size.padding,
          "font-sans-v4 text-s-v4 transition-colors",
          "text-text-secondary hover:text-text-primary data-[state=active]:text-text-primary",
          "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand-border",
          "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-text-secondary",
          "[&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5",
          fill && "flex-1",
          className,
        )}
        onMouseEnter={(event) => {
          // The pill doesn't follow a disabled tab; it goes back to the
          // selected one.
          if (disabled) clearHover();
          else onTabMouseEnter?.();
          onMouseEnter?.(event);
        }}
        {...props}
      >
        {icon}
        {children}
        {count != null && <TabStripCount count={count} />}
      </TabsPrimitive.Trigger>
    );
  },
);

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(function TabsContent({ className, value, forceMount, ...props }, ref) {
  const root = React.useContext(TabsRootContext);
  const registerPanel = root?.registerPanel;
  React.useLayoutEffect(
    () => registerPanel?.(value, forceMount === true),
    [registerPanel, value, forceMount],
  );

  return (
    <TabsPrimitive.Content
      ref={ref}
      value={value}
      forceMount={forceMount}
      className={cn(
        "rounded-lg focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand-border",
        className,
      )}
      {...props}
    />
  );
});

/** The count chip a tab renders for `count`, for custom tab content. */
const TabsCount = TabStripCount;

export { Tabs, TabsList, TabsTrigger, TabsContent, TabsCount };
